/**
 * CheckpointMapEditor.jsx
 *
 * Click-to-place checkpoint editor for the customer portal's map detail page —
 * the same interaction admin-fe's PgmViewer offers (click the map to drop a
 * pin, drag the arrow to set heading, name it, save), rebuilt here so the
 * portal doesn't depend on admin-only components (PaintEditor/MaskEditor,
 * admin's own zoom/pan hook). Coordinate math and drawing are shared via
 * utils/mapCoords.js so a pin placed here lands at the exact same world
 * coordinate as one placed in the admin portal.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { load as yamlLoad } from "js-yaml";
import { Box, TextField, Button, IconButton, CircularProgress, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import CONFIG from "../../config/config";
import { tokens } from "../../theme";
import useMapZoomPan from "../../hooks/useMapZoomPan";
import {
  pxToWorld,
  worldToPx,
  drawPoseMarker,
  angleFromPointer,
  degToRad,
} from "../../utils/mapCoords";

const BASE_URL = CONFIG.BASE_URL;

const parsePGM = async (pgmBlob) => {
  const arrayBuffer = await pgmBlob.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);
  const headerString = new TextDecoder("ascii").decode(data.subarray(0, 2048));
  const lines = headerString.split(/\r?\n/).filter((l) => l.length > 0 && !l.startsWith("#"));
  if (lines[0].trim() !== "P5") throw new Error("Not a valid P5 PGM file");
  const [width, height] = lines[1].trim().split(/\s+/).map(Number);
  const maxGray = Number.parseInt(lines[2].trim(), 10);
  if (!Number.isFinite(width) || !Number.isFinite(height) || !Number.isFinite(maxGray)) {
    throw new TypeError("Invalid PGM header");
  }
  const headerLength = headerString.indexOf(lines[2]) + lines[2].length + 1;
  const pixelsRaw = data.subarray(headerLength, headerLength + width * height);
  const pixels = Array.from(pixelsRaw, (v) => (v / maxGray) * 255);
  return { width, height, pixels };
};

const CLICK_RADIUS_PX = 15;

/**
 * @param {string} mapId
 * @param {Array} locations  the map's mapCoordinate array — owned by the parent
 * @param {(loc: object) => void} onAddLocation      loc = { [name]: [x,y,z,yaw] }
 * @param {(index: number, loc: object) => void} onUpdateLocation
 * @param {(index: number) => void} onDeleteLocation
 */
const CheckpointMapEditor = ({
  mapId,
  locations,
  onAddLocation,
  onUpdateLocation,
  onDeleteLocation,
}) => {
  const nameInputRef = useRef(null);
  const [pgmData, setPgmData] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tempPoint, setTempPoint] = useState(null);
  const [rotating, setRotating] = useState(false);
  const tempPointRef = useRef(null);
  useEffect(() => {
    tempPointRef.current = tempPoint;
  }, [tempPoint]);

  const { wrapRef, canvasRef, toCanvasPoint, isPanning, zoomIn, zoomOut, fitToView } =
    useMapZoomPan({
      mapSize: pgmData,
      panEnabled: !tempPoint,
      allowUpscale: true,
    });

  // ── Fetch + parse the map's PGM raster and YAML metadata ──────────────────
  useEffect(() => {
    if (!mapId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    const token = localStorage.getItem("portalToken");
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [yamlRes, pgmRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/maps/base64-to-yaml/${mapId}`, {
            responseType: "text",
            headers,
          }),
          axios.get(`${BASE_URL}/api/maps/convertbase64ToPgm/${mapId}`, {
            responseType: "blob",
            headers,
          }),
        ]);
        if (cancelled) return;
        setMapConfig(yamlLoad(yamlRes.data));
        setPgmData(await parsePGM(pgmRes.data));
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mapId]);

  // ── Draw ────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!pgmData || !canvas) return;
    const { width, height, pixels } = pgmData;
    const w = Math.floor(Number(width));
    const h = Math.floor(Number(height));
    if (!Number.isFinite(w) || !Number.isFinite(h)) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    if (pixels) {
      const imageData = ctx.createImageData(w, h);
      for (let i = 0; i < pixels.length; i++) {
        const v = pixels[i];
        imageData.data[i * 4] = v;
        imageData.data[i * 4 + 1] = v;
        imageData.data[i * 4 + 2] = v;
        imageData.data[i * 4 + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
    }

    (locations || []).forEach((loc) => {
      const name = Object.keys(loc)[0];
      const [x, y, , yaw] = loc[name];
      const { x: px, y: py } = worldToPx(x, y, mapConfig, h);
      drawPoseMarker(ctx, px, py, yaw || 0, { color: "#2563eb", label: name });
    });

    if (tempPoint) {
      const yawRad = degToRad(tempPoint.yawDeg);
      drawPoseMarker(ctx, tempPoint.x, tempPoint.y, yawRad, {
        color: "#f59e0b",
        label: tempPoint.name || undefined,
        showHeading: true,
      });
    }
  }, [pgmData, mapConfig, locations, tempPoint, canvasRef]);

  useEffect(() => {
    draw();
  }, [draw]);

  // ── Click to place / select a pin ──────────────────────────────────────
  const handleCanvasClick = (e) => {
    if (!pgmData || !mapConfig || e.target !== canvasRef.current || isPanning()) return;
    const { x: clickX, y: clickY } = toCanvasPoint(e);
    const { height } = pgmData;

    const hitIndex = (locations || []).findIndex((loc) => {
      const name = Object.keys(loc)[0];
      const [x, y] = loc[name];
      const { x: px, y: py } = worldToPx(x, y, mapConfig, height);
      return Math.hypot(clickX - px, clickY - py) < CLICK_RADIUS_PX;
    });

    if (hitIndex >= 0) {
      const loc = locations[hitIndex];
      const name = Object.keys(loc)[0];
      const [x, y, , yaw = 0] = loc[name];
      const { x: px, y: py } = worldToPx(x, y, mapConfig, height);
      setTempPoint({
        x: px,
        y: py,
        yawDeg: Math.round((yaw * 180) / Math.PI),
        name,
        editIndex: hitIndex,
      });
      setTimeout(() => nameInputRef.current?.focus(), 10);
      return;
    }

    if (tempPoint) return; // one pending pin at a time

    setTempPoint({ x: clickX, y: clickY, yawDeg: 0, name: "", editIndex: null });
    setTimeout(() => nameInputRef.current?.focus(), 10);
  };

  // ── Drag the pending pin's arrow to set yaw ────────────────────────────
  const rotatingRef = useRef(false);
  const toCanvasPointRef = useRef(toCanvasPoint);
  useEffect(() => {
    toCanvasPointRef.current = toCanvasPoint;
  }, [toCanvasPoint]);

  const handleMouseDown = (e) => {
    const tp = tempPointRef.current;
    if (!tp) return;
    const { x, y } = toCanvasPoint(e);
    if (Math.hypot(x - tp.x, y - tp.y) < 20) {
      rotatingRef.current = true;
      setRotating(true);
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!rotatingRef.current) return;
      const tp = tempPointRef.current;
      if (!tp) return;
      const { x, y } = toCanvasPointRef.current(e);
      const angleDeg = angleFromPointer(tp.x, tp.y, x, y);
      setTempPoint((p) => (p ? { ...p, yawDeg: angleDeg } : p));
    };
    const onUp = () => {
      rotatingRef.current = false;
      setRotating(false);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // ── Confirm / cancel / delete ───────────────────────────────────────────
  const confirmPoint = () => {
    if (!tempPoint?.name?.trim() || !pgmData) return;
    const {
      x: mapX,
      y: mapY,
      z: mapZ,
    } = pxToWorld(tempPoint.x, tempPoint.y, mapConfig, pgmData.height);
    const yaw = degToRad(tempPoint.yawDeg);
    const locData = { [tempPoint.name.trim()]: [mapX, mapY, mapZ, yaw] };
    if (tempPoint.editIndex !== null) {
      onUpdateLocation?.(tempPoint.editIndex, locData);
    } else {
      onAddLocation?.(locData);
    }
    setTempPoint(null);
  };

  const deletePoint = () => {
    if (tempPoint?.editIndex !== null && tempPoint?.editIndex !== undefined) {
      onDeleteLocation?.(tempPoint.editIndex);
    }
    setTempPoint(null);
  };

  const cancelPoint = () => setTempPoint(null);

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: tokens.accent }} size={28} />
      </Box>
    );
  }
  if (loadError || !pgmData) {
    return (
      <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: 13, color: tokens.text3 }}>
          Couldn't load the map image
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", minHeight: 0 }}>
      <button
        type="button"
        ref={wrapRef}
        aria-label="Map — click to place a checkpoint, drag or scroll to pan/zoom"
        onClick={handleCanvasClick}
        onMouseDown={handleMouseDown}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          background: tokens.surface2,
          border: "none",
          padding: 0,
          cursor: rotating ? "grabbing" : "grab",
          touchAction: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            imageRendering: "pixelated",
            transformOrigin: "0 0",
          }}
        />
      </button>

      {/* Zoom controls */}
      <Box sx={{ position: "absolute", bottom: 10, left: 10, display: "flex", gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={zoomIn}
          sx={{
            background: tokens.surface,
            border: `1px solid ${tokens.border}`,
            "&:hover": { background: tokens.surface2 },
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={zoomOut}
          sx={{
            background: tokens.surface,
            border: `1px solid ${tokens.border}`,
            "&:hover": { background: tokens.surface2 },
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={fitToView}
          sx={{
            background: tokens.surface,
            border: `1px solid ${tokens.border}`,
            "&:hover": { background: tokens.surface2 },
          }}
        >
          <CenterFocusStrongIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Pending checkpoint panel */}
      {tempPoint && (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            background: tokens.surface,
            border: `1px solid ${tokens.border}`,
            borderRadius: 2.5,
            p: 1.75,
            minWidth: 220,
            boxShadow: tokens.shadowLg,
            zIndex: 10,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: tokens.text3,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {tempPoint.editIndex !== null ? "Edit checkpoint" : "New checkpoint"}
          </Typography>
          <TextField
            inputRef={nameInputRef}
            size="small"
            fullWidth
            placeholder="Checkpoint name"
            value={tempPoint.name}
            onChange={(e) => setTempPoint((p) => ({ ...p, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && confirmPoint()}
          />
          <Typography sx={{ fontSize: 11, color: tokens.text3, mt: 0.75, mb: 1 }}>
            Drag the arrow to set facing direction
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button
              size="small"
              variant="contained"
              onClick={confirmPoint}
              disabled={!tempPoint.name.trim()}
              sx={{ flex: 1 }}
            >
              Save
            </Button>
            {tempPoint.editIndex !== null && (
              <Button size="small" color="error" onClick={deletePoint}>
                Delete
              </Button>
            )}
            <Button size="small" onClick={cancelPoint} sx={{ color: tokens.text2 }}>
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

CheckpointMapEditor.propTypes = {
  mapId: PropTypes.string.isRequired,
  locations: PropTypes.array,
  onAddLocation: PropTypes.func,
  onUpdateLocation: PropTypes.func,
  onDeleteLocation: PropTypes.func,
};

export default CheckpointMapEditor;
